import { Icon, Timestamp, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { accessControl } from 'lib-shared-access-control';
import { AsyncStatus, MetaConversation } from 'lib-shared-legacy-types';
import { Component } from 'react';

import { numberFormatterInternational } from 'app/utils/telephonyHelpers';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';

import Spinner from '../../../ajax/components/Spinner';
import styles from '../styles/phone-control.module.scss';

type Props = {
  meta: {
    asyncStatus: AsyncStatus;
    usersAsyncStatus: AsyncStatus;

    dynamicTelephoneNumbersArrayAsyncStatus: AsyncStatus;
    allCountryCodesAsyncStatus: AsyncStatus;
    mute: AsyncStatus;
    hold: AsyncStatus;
  };
  isOnHold: boolean;
  isOnMute: boolean;
  isOnTrial: boolean;
  metaConversation: MetaConversation;
  isInternalCall: boolean;
  isWarmTransferAvailable: boolean;
  hasAddCaller: boolean;

  telephonySetMute: (payload: { setTo: boolean; csid: any }) => Promise<any>;
  telephonySetHold: (payload: {
    setTo: boolean;
    csid: any;
    requesterId: any;
  }) => Promise<any>;
  conversationClose: (payload: { csid: any }) => void;

  notificationAdd: (payload: any) => void;

  transferToNumberModalOpen: (metaConversation: MetaConversation) => void;
  transferToQueueModalOpen: (payload: {
    csid: any;
    isWarmTransferAvailable?: boolean;
  }) => void;
  transferToAgentModalOpen: (metaConversation: MetaConversation) => void;
  addCallerModalOpen: (metaConversation: MetaConversation) => void;
  telephonySendDigits: (digit: string) => void;
};

type StateType = {
  disabled: boolean;
  phoneWidgetHolderClass: string;
  dial: {
    isOpen: boolean;
    btnHolderClass: string;
    btnClass: string;
  };
  transfer: {
    isOpen: boolean;
    btnHolderClass: string;
    btnClass: string;
  };
};

export default class Phone extends Component<Props, StateType> {
  constructor(props: Props) {
    super(props);
    this.state = {
      disabled: true,
      phoneWidgetHolderClass: '',
      dial: {
        isOpen: false,
        btnHolderClass: '',
        btnClass: '',
      },
      transfer: {
        isOpen: false,
        btnHolderClass: '',
        btnClass: '',
      },
    };
  }

  componentWillUnmount() {
    this.setState({
      disabled: true,
      phoneWidgetHolderClass: '',
      dial: {
        isOpen: false,
        btnHolderClass: '',
        btnClass: '',
      },
      transfer: {
        isOpen: false,
        btnHolderClass: '',
        btnClass: '',
      },
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      !nextProps.metaConversation.conversation.is_closed &&
      nextProps.metaConversation.isAssignedToMe
    ) {
      this.setState({ disabled: false });
    } else {
      this.setState({ disabled: true });
    }
  }

  componentDidMount() {
    if (
      !this.props.metaConversation.conversation.is_closed &&
      this.props.metaConversation.isAssignedToMe
    ) {
      this.setState({ disabled: false });
    }
  }

  clickHandlerHangup = (e: any) => {
    this.setState({ disabled: true });
    e.preventDefault();
    this.props.conversationClose({
      csid: this.props.metaConversation.conversation.id,
    });
  };

  clickHandlerHold = (e: any) => {
    this.props
      .telephonySetHold({
        setTo: !this.props.isOnHold,
        csid: this.props.metaConversation.conversation.id,
        requesterId: this.props.metaConversation?.requester?.id,
      })
      .then()
      .catch(() => {
        const option = {
          type: 'mute_hold_issue',
          title: 'Hold could not be changed',
          text: 'Hold not changed',
          className: 'warning',
          icon: 'i-announcement-slash',
          time: 10000,
        };
        this.props.notificationAdd(option);
      });
    e.preventDefault();
  };

  clickHandlerMute = (e: any) => {
    this.props
      .telephonySetMute({
        setTo: !this.props.isOnMute,
        csid: this.props.metaConversation.conversation.id,
      })
      .then()
      .catch(() => {
        const option = {
          type: 'mute_hold_issue',
          title: 'Mute could not be changed',
          text: 'Mute not changed',
          className: 'warning',
          icon: 'i-announcement-slash',
          time: 10000,
        };
        this.props.notificationAdd(option);
      });
    e.preventDefault();
  };

  clickHandlerDialPad = (e: any) => {
    e.preventDefault();
    if (this.state.dial.isOpen) {
      this.setState({
        dial: {
          isOpen: false,
          btnHolderClass: 'fadeOutUp',
          btnClass: '',
        },
        transfer: {
          isOpen: false,
          btnHolderClass: 'fadeOutUp',
          btnClass: '',
        },
        phoneWidgetHolderClass: '',
      });
    } else {
      this.setState({
        dial: {
          isOpen: true,
          btnHolderClass: 'fadeInDown',
          btnClass: 'active',
        },
        transfer: {
          isOpen: false,
          btnHolderClass: 'fadeOutUp',
          btnClass: '',
        },
        phoneWidgetHolderClass: 'open',
      });
    }
  };

  clickHandlerTransfer = (e: any) => {
    e.preventDefault();
    if (this.state.transfer.isOpen) {
      this.setState({
        transfer: {
          isOpen: false,
          btnHolderClass: 'fadeOutUp',
          btnClass: '',
        },
        dial: {
          isOpen: false,
          btnHolderClass: 'fadeOutUp',
          btnClass: '',
        },
        phoneWidgetHolderClass: '',
      });
    } else {
      this.setState({
        transfer: {
          isOpen: true,
          btnHolderClass: 'fadeInDown',
          btnClass: 'active',
        },
        dial: {
          isOpen: false,
          btnHolderClass: 'fadeOutUp',
          btnClass: '',
        },
        phoneWidgetHolderClass: 'open',
      });
    }
  };

  render() {
    let phoneNumberFormat = '';
    let staticTime: any = null;
    let isRecording: JSX.Element | null = null;

    const metaConv = this.props.metaConversation;

    const phoneNumber =
      metaConv?.conversation.attributes?.called_number ??
      metaConv?.requester?.phone_number;

    if (!this.props.isInternalCall) {
      if (phoneNumber) {
        if (phoneNumber.startsWith('+')) {
          phoneNumberFormat = numberFormatterInternational({
            phoneNumber,
          });
        } else {
          phoneNumberFormat = phoneNumber || 'anonymous';
        }
      } else {
        phoneNumberFormat = 'anonymous';
      }
    } else {
      phoneNumberFormat = 'Internal';
    }

    const listDigits = (digit: {
      className: string;
      input: string;
      sec_line: string;
    }) => {
      return (
        <button
          className={digit.className}
          onClick={() => {
            this.props.telephonySendDigits(digit.input);
          }}
        >
          <span className={styles.digit}>{digit.input}</span>
          <span className={styles.sec_line}>{digit.sec_line}</span>
        </button>
      );
    };

    let canTransferToNumberConversation = false;
    let canTransferToQueueConversation = false;
    let canTransferToAgentConversation = false;

    if (
      !this.props.metaConversation.conversation.is_closed &&
      this.props.metaConversation.isAssignedToMe &&
      !this.props.isInternalCall &&
      this.props.metaConversation.conversation.initial_channel === 'pstnphone'
    ) {
      canTransferToNumberConversation = true;
      canTransferToQueueConversation = true;
      canTransferToAgentConversation = true;
    }

    if (
      !this.props.metaConversation.conversation.is_closed &&
      this.props.metaConversation.conversation.assignment &&
      this.props.metaConversation.conversation.assignment.assigned_at
    ) {
      staticTime =
        this.props.metaConversation.conversation.assignment.assigned_at;
    }

    if (
      this.props?.metaConversation?.conversation?.attributes
        ?.record_conversation === 'true'
    ) {
      isRecording = <Icon name="rec" size={24} />;
    }

    if (this.props.isOnTrial) {
      canTransferToNumberConversation = false;
    }

    const hasConversationReplyAccess =
      accessControl.hasAccess('conversation.reply');

    const isTransferDisabled =
      this.state.disabled ||
      this.props.isInternalCall ||
      this.props.metaConversation.conversation.initial_channel !== 'pstnphone';
    const isAddCallDisabled =
      this.state.disabled || !hasConversationReplyAccess;

    return (
      <div className={styles.phoneWidgetContainer}>
        <div
          className={classnames(styles.phoneWidgetHolder, {
            [styles.open]: this.state.phoneWidgetHolderClass === 'open',
          })}
        >
          <div className={styles.alwaysVisiblePart}>
            <div className={styles.phoneWidgetDisplay}>
              <h3>{phoneNumberFormat}</h3>
              {isRecording}
              <h5 id="timer">
                <Timestamp counter time={staticTime} />
              </h5>
            </div>
            <div className={styles.mainButtonsHolder}>
              <Tooltip
                content={
                  <CommandKeybindingsTooltipContent commandName="conversation:phoneToggleHold" />
                }
              >
                <button
                  className={classnames(styles.left, {
                    [styles.active]: this.props.isOnHold,
                    [styles.disabled]: this.state.disabled,
                  })}
                  disabled={this.state.disabled}
                  onClick={
                    this.props.metaConversation.conversation.is_closed ===
                      false && this.props.metaConversation.isAssignedToMe
                      ? this.clickHandlerHold
                      : () => {
                          /** Noop. */
                        }
                  }
                >
                  <Icon name="pause" size={24} />
                  {this.props.meta.hold === 'loading' && <Spinner />}
                  <span>
                    {this.props.isOnHold === true ? 'UNHOLD' : 'HOLD'}
                  </span>
                </button>
              </Tooltip>
              <Tooltip
                content={
                  <CommandKeybindingsTooltipContent commandName="conversation:close" />
                }
              >
                <button
                  className={classnames({
                    [styles.middle]: true,
                    [styles.disabled]: this.state.disabled,
                  })}
                  disabled={this.state.disabled}
                  onClick={
                    !this.props.metaConversation.conversation.is_closed &&
                    this.props.metaConversation.isAssignedToMe
                      ? this.clickHandlerHangup
                      : () => {
                          /** Noop. */
                        }
                  }
                >
                  <Icon name="phone-hang" size={24} />
                  <span>END</span>
                </button>
              </Tooltip>
              <Tooltip
                content={
                  <CommandKeybindingsTooltipContent commandName="conversation:phoneToggleMute" />
                }
              >
                <button
                  className={classnames({
                    [styles.right]: true,
                    [styles.active]: this.props.isOnMute,
                    [styles.disabled]: this.state.disabled,
                  })}
                  disabled={this.state.disabled}
                  onClick={
                    this.props.metaConversation.conversation.is_closed ===
                      false &&
                    this.props.metaConversation.isAssignedToMe === true
                      ? this.clickHandlerMute
                      : () => {
                          /** Noop. */
                        }
                  }
                >
                  <Icon name="volume-mute" size={24} />
                  {this.props.meta.mute === 'loading' && (
                    <Spinner className="dark close-btn" />
                  )}
                  <span>{this.props.isOnMute ? 'UNMUTE' : 'MUTE'}</span>
                </button>
              </Tooltip>
              <button
                className={classnames(styles.reassignBtn, styles.left, {
                  [styles[this.state.transfer.btnClass]]:
                    !!this.state.transfer.btnClass,
                  [styles.disabled]: isTransferDisabled,
                })}
                disabled={isTransferDisabled}
                onClick={this.clickHandlerTransfer}
              >
                <Icon name="user-plus" size={24} />
                <span>TRANSFER</span>
              </button>
              <button
                className={classnames(styles.middle, {
                  [styles[this.state.dial.btnClass]]:
                    !!this.state.dial.btnClass,
                  [styles.disabled]: this.state.disabled,
                })}
                disabled={this.state.disabled}
                id="demo-dial"
                onClick={this.clickHandlerDialPad}
              >
                <Icon name="th" size={24} />
                <span>DIAL</span>
              </button>
              <Tooltip
                disabled={hasConversationReplyAccess}
                content="You don't have permissions to add call participants"
              >
                <button
                  className={classnames(styles.openBtn, styles.right, {
                    [styles.disabled]: isAddCallDisabled,
                  })}
                  disabled={isAddCallDisabled}
                  onClick={() =>
                    this.props.addCallerModalOpen(this.props.metaConversation)
                  }
                >
                  <Icon name="phone-plus" size={24} />
                  <span>ADD CALL</span>
                </button>
              </Tooltip>
            </div>
          </div>
          <div
            className={classnames(styles.dialButtonsHolder, {
              [styles[this.state.dial.btnHolderClass]]:
                !!this.state.dial.btnHolderClass,
              [styles.open]: !!this.state.dial.isOpen,
            })}
          >
            {[
              {
                input: '1',
                sec_line: '',
                className: `${styles.left}`,
              },
              {
                input: '2',
                sec_line: 'ABC',
                className: ` ${styles.middle}`,
              },
              {
                input: '3',
                sec_line: 'DEF',
                className: classnames(styles.btn3, styles.right),
              },
              {
                input: '4',
                sec_line: 'GHI',
                className: classnames(styles.btn4, styles.left),
              },
              {
                input: '5',
                sec_line: 'JKL',
                className: `${styles.btn5} ${styles.middle}`,
              },
              {
                input: '6',
                sec_line: 'MNO',
                className: classnames(styles.btn6, styles.right),
              },
              {
                input: '7',
                sec_line: 'PQRS',
                className: classnames(styles.btn7, styles.left),
              },
              {
                input: '8',
                sec_line: 'TUV',
                className: classnames(styles.btn8, styles.middle),
              },
              {
                input: '9',
                sec_line: 'WXYZ',
                className: classnames(styles.btn9, styles.right),
              },
              {
                input: 'any',
                sec_line: '',
                className: classnames(
                  styles.btnStar,
                  styles.left,
                  styles.lastLine,
                ),
              },
              {
                input: '0',
                sec_line: '',
                className: classnames(styles.middle, styles.lastLine),
              },
              {
                input: '#',
                sec_line: '',
                className: classnames(
                  styles.btnHash,
                  styles.right,
                  styles.lastLine,
                ),
              },
            ].map(listDigits)}
          </div>
          <div
            className={classnames(
              styles.transferHolder,
              styles[this.state.dial.btnHolderClass],
              {
                [styles['open']]: this.state.transfer.isOpen,
              },
            )}
          >
            <button
              className={classnames(styles.longBtn, {
                [styles['disabled']]: !canTransferToAgentConversation,
              })}
              onClick={
                canTransferToAgentConversation
                  ? () => {
                      this.props.transferToAgentModalOpen(
                        this.props.metaConversation,
                      );
                    }
                  : () => {
                      /** Noop. */
                    }
              }
            >
              Transfer to agent
            </button>
            <button
              className={classnames(styles['longBtn'], {
                [styles['disabled']]: !canTransferToQueueConversation,
              })}
              onClick={
                canTransferToQueueConversation
                  ? () => {
                      this.props.transferToQueueModalOpen({
                        csid: this.props.metaConversation.conversation.id,
                        isWarmTransferAvailable:
                          this.props.isWarmTransferAvailable,
                      });
                    }
                  : () => {
                      /** Noop. */
                    }
              }
            >
              Transfer to queue
            </button>
            <button
              className={classnames(styles['longBtn'], styles['last'], {
                [styles['disabled']]: !canTransferToNumberConversation,
              })}
              onClick={
                canTransferToNumberConversation
                  ? () => {
                      this.props.transferToNumberModalOpen(
                        this.props.metaConversation,
                      );
                    }
                  : () => {
                      /** Noop. */
                    }
              }
            >
              Transfer to number
            </button>
          </div>
        </div>
      </div>
    );
  }
}
